import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMXN } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { usePlanActual } from "../lib/planes";
import PageHeader from "../components/PageHeader";
import LockedFeature from "../components/LockedFeature";
import { toast } from "sonner";
import { Sparkles, Megaphone, RefreshCw } from "lucide-react";

const reasonFor = (p) => {
  if (p.status === "Interesado") return "Preguntó precio y no agendó";
  if (p.status === "No asistió") return "No asistió a su cita";
  if (p.status === "Recuperación") return "Sin seguimiento o cancelación";
  if (p.status === "Inactivo") return "Sin cita en 90+ días";
  return "Oportunidad";
};

// `aiMessage()` RETIRADO (auditoria de copy, 12 Sep 2026). Era el texto por defecto del
// "Mensaje IA sugerido" y ofrecia horarios concretos -- "el miercoles a las 16:00",
// "manana 11:00 o el jueves 17:30" -- que nunca se consultaron contra `compute_slots`, y
// saludaba en nombre de "Clinica Altura" en CUALQUIER clinica. Copiado tal cual, prometia
// al paciente un hueco que puede estar ocupado. Ahora o hay mensaje generado de verdad por
// Claude (POST /recovery/patients/{id}/generate, que si existe), o no se muestra ninguno.

export default function Recovery() {
  const { sinSofi } = usePlanActual(); // TEN.4
  const [items, setItems] = useState([]);
  const [llmCache, setLlmCache] = useState({}); // patient_id -> generated message
  const [loadingId, setLoadingId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const { activeClinic } = useAuth();
  const currentPlan = activeClinic?.plan_slug || "starter";

  const load = async () => {
    try {
      // GET /recovery/campaigns se retiro del backend junto con la seccion: ya no se pide.
      const a = await api.get("/recovery/patients");
      setItems(a.data.items || []);
    } catch {
      setLoadError("No se pudieron cargar los datos de recuperación");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const totalOpp = items.reduce((acc, p) => acc + (p.estimated_value || 0), 0);

  // `sendFollowup()` RETIRADO (auditoria de copy, 12 Sep 2026): llamaba a POST /recovery/send,
  // que decia "enviado" y solo hacia UPDATE patients SET recovered=true. Ese endpoint responde
  // 501 desde este mismo bloque, asi que dejar el boton habria cambiado una mentira por un error.

  const regenerateAI = async (p) => {
    setLoadingId(p.id);
    try {
      const { data } = await api.post(`/recovery/patients/${p.id}/generate`);
      setLlmCache((prev) => ({ ...prev, [p.id]: data.message }));
      toast.success("Mensaje generado con Claude Haiku");
    } catch {
      toast.error("No se pudo generar el mensaje");
    } finally {
      setLoadingId(null);
    }
  };

  if (loadError) return <div className="p-8 text-rose-500">{loadError}</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full">
      <PageHeader
        title="Recuperación de pacientes"
        subtitle="Pacientes con oportunidad de reactivación, ordenados por valor estimado."
      />

      {/* Los dos KPIs de campañas se retiraron con ellas (auditoría de copy, 12 Sep 2026):
          "Campañas activas" y "Recuperados (mes)" sumaban las cifras sembradas a mano, y
          el segundo decía "(mes)" sin ninguna ventana temporal detrás. Los dos que quedan
          salen de `patients` real. */}
      <div className="grid grid-cols-2 gap-3">
        <Mini label="Pacientes en oportunidad" value={items.length} />
        <Mini label="Oportunidad estimada" value={formatMXN(totalOpp)} />
      </div>

      {/* ── CAMPAÑAS DE RECUPERACIÓN — PRÓXIMAMENTE ─────────────────────────────────
          🔴 NO BORRAR: esta sección se construye después, y lo que había aquí explica
          por qué se retiró.

          Hasta el 12 Sep 2026 esto pintaba 7 campañas con sus cifras de "Audiencia",
          "Recuperados" e "Ingreso", y tres botones. Nada de eso existía:

            · las 7 campañas y TODAS sus cifras eran datos sembrados a mano (seed_data.py),
              no resultados de la clínica — el seed se retiró en este mismo bloque;
            · "Pausar"/"Activar" y "Lanzar ahora" eran `toast.success()` a secas, sin una
              sola llamada al backend: decían "Mensajes enviados a la audiencia" y no se
              enviaba nada.

          Una sección que dice "Próximamente" es infinitamente mejor que una que simula
          funcionar. `GET /recovery/campaigns` se retiró del backend, así que aquí ya no
          se pide nada. Cuando se construya, el envío real es lo primero que hay que tener
          en pie — no los botones. ── */}
      <LockedFeature requiredPlan="pro" currentPlan={currentPlan}>
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="font-display font-bold inline-flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-brand-electric" /> Campañas de recuperación
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold bg-slate-100 text-slate-500 border border-slate-200">
              Próximamente
            </span>
          </div>
          <div className="px-5 py-8 text-center" data-testid="campaigns-coming-soon">
            {!sinSofi && (<>
            <p className="text-sm text-slate-600 max-w-xl mx-auto">
              Podrás agrupar a los pacientes de abajo en campañas y enviarles seguimiento por
              WhatsApp de forma automática. Todavía no está disponible.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Mientras tanto, puedes contactarlos uno a uno desde la Bandeja de WhatsApp.
            </p>
            </>)}
          </div>
        </div>
      </LockedFeature>

      {/* Patients list */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Pacientes con oportunidad</div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No hay pacientes pendientes de recuperación.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((p) => (
              <li key={p.id} className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-3 items-start" data-testid={`recovery-row-${p.id}`}>
                <div className="lg:col-span-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 grid place-items-center font-semibold">{p.name[0]}</div>
                  <div>
                    <Link to={`/app/pacientes/${p.id}`} className="font-medium text-slate-900 hover:text-brand-electric">{p.name}</Link>
                    <div className="text-xs text-slate-500">{p.phone}</div>
                  </div>
                </div>
                <div className="lg:col-span-2 text-sm">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Motivo</div>
                  <div className="text-slate-700">{reasonFor(p)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Última: {new Date(p.last_interaction).toLocaleDateString("es-MX")}</div>
                </div>
                <div className="lg:col-span-5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Mensaje IA sugerido {llmCache[p.id] && <span className="bg-indigo-200 text-indigo-800 px-1.5 py-0.5 rounded text-[9px] font-mono normal-case tracking-normal ml-1">Claude</span>}</div>
                    <button
                      onClick={() => regenerateAI(p)}
                      disabled={loadingId === p.id}
                      className="text-[10px] font-medium text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid={`recovery-regen-${p.id}`}
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingId === p.id ? "animate-spin" : ""}`} /> {loadingId === p.id ? "Generando…" : "Regenerar IA"}
                    </button>
                  </div>
                  {/* Sin fallback inventado: o hay mensaje generado de verdad por Claude, o
                      se ofrece generarlo. El texto por defecto que vivía aquí ofrecía horarios
                      concretos ("el miércoles a las 16:00") que NUNCA se consultaron contra la
                      agenda, y saludaba en nombre de "Clínica Altura" fuera cual fuera la
                      clínica. Copiarlo tal cual habría prometido un hueco que puede no existir. */}
                  {llmCache[p.id] ? (
                    <div className="rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-900 px-3 py-2 mt-1 leading-snug">{llmCache[p.id]}</div>
                  ) : (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 text-slate-500 px-3 py-2 mt-1 leading-snug text-xs">
                      Genera un mensaje con IA para este paciente con “Regenerar IA”.
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2 flex flex-col items-end gap-1.5">
                  <div className="text-xs text-slate-500">Valor estimado</div>
                  <div className="font-display text-xl font-bold">{formatMXN(p.estimated_value)}</div>
                  {/* "Enviar" y "Agendar" RETIRADOS (auditoría de copy, 12 Sep 2026).
                        · "Enviar" decía "Seguimiento enviado a X" y lo único que ejecutaba era
                          UPDATE patients SET recovered=true — marcaba al paciente como recuperado
                          SIN CONTACTARLO, y esa marca alimentaba dos KPIs de Analítica.
                          Su endpoint ahora responde 501.
                        · "Agendar" no abria ningun modal: mostraba como confirmacion el texto
                          de marcador que el desarrollador habia dejado en su sitio, visible tal
                          cual para el cliente. (El literal NO se reproduce aqui a proposito:
                          citarlo lo devuelve al siguiente grep y al bundle -- regla de J1-B.)
                      En su lugar, dos caminos que SÍ funcionan hoy. */}
                  {/* Sólo un camino, y es uno que funciona hoy. Se descartó enlazar directo a
                      la bandeja con el teléfono: Inbox sólo lee `?conv=`, y en modo real ese
                      valor tiene que ser un teléfono que YA tenga conversación abierta — con un
                      paciente que nunca ha escrito, el enlace fallaría. Desde la ficha sí hay
                      "Ver bandeja" cuando la conversación existe de verdad. */}
                  <Link
                    to={`/app/pacientes/${p.id}`}
                    className="text-xs font-medium inline-flex items-center gap-1 border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50 mt-1"
                    data-testid={`recovery-open-patient-${p.id}`}
                  >
                    Ver ficha
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-4">
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
